import cuid from "cuid";
import { cloneDeep, get, isBoolean, isEmpty, isNumber, set } from "lodash";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useLazyQuery, useMutation, useQuery } from "shared/hooks/useApi";
import { StoreContext } from "shared/store";
import graphql from "utils/api/graphql";
import {
  CREATE_ATTACHMENT,
  UNLINK_ATTACHMENT_FROM_INSURANCE_PLAN,
} from "utils/api/graphql/mutations/attachment";
import { CREATE_OFFER, DELETE_OFFER } from "utils/api/graphql/mutations/offers";
import { ATTACHMENTS } from "utils/api/graphql/queries/attachments";
import { OFFERS } from "utils/api/graphql/queries/offers";
import {
  DELTA_END_TYPES,
  DOCUMENT_STEPS,
  INITIAL_VALUES,
  INSURANCE_PLAN_ATTACHMENTS_TYPES,
  INSURANCE_PLAN_PARAMS_RULES,
  PERIOD_UNITS,
  SCREENS,
} from "utils/constants";
import { DEFAULT_DOCUMENT_STEP_FLAGS } from "utils/helpers/documentStepsConfig";
import View from "./View";

const DELTA_END_VALUES = {
  [DELTA_END_TYPES.END_OF_YEAR]: () => 0,
  [DELTA_END_TYPES.ADD_PERIOD]: (start, end, unit = PERIOD_UNITS.DAYS) => {
    if (!isNumber(end) || !isNumber(start)) return null;
    return moment()
      .add(start || 0, "days")
      .add(end, unit)
      .diff(moment().startOf("day"), "days");
  },
  DEFAULT: () => null,
};

const calculateDeltaEndValue = (rules, startRuleKey, endRuleKey) => {
  const type = get(rules, `${endRuleKey}.type`) || "DEFAULT";
  return DELTA_END_VALUES[type](
    get(rules, `${startRuleKey}.value`),
    get(rules, `${endRuleKey}.value`),
    get(rules, `${endRuleKey}.unit`),
  );
};

/** Persist quote-validity UI as config only — date is resolved at quote generation. */
const serializeQuoteValidityConfig = (rules) => {
  const type = get(rules, `${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.type`);
  if (type === DELTA_END_TYPES.END_OF_YEAR) {
    return { type: DELTA_END_TYPES.END_OF_YEAR };
  }
  if (type === DELTA_END_TYPES.ADD_PERIOD) {
    const value = Number(
      get(rules, `${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.value`),
    );
    if (!Number.isFinite(value)) return null;
    return {
      type: DELTA_END_TYPES.ADD_PERIOD,
      value,
      unit:
        get(rules, `${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.unit`) ||
        PERIOD_UNITS.DAYS,
      limitEndOfYear: !!get(
        rules,
        `${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.limitEndOfYear`,
      ),
    };
  }
  return null;
};

const updateRulesWithDeltaValues = (rules) => {
  const newRules = cloneDeep(rules);
  const deltaEndValue = calculateDeltaEndValue(
    newRules,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_START,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_END,
  );
  if (deltaEndValue !== null) {
    set(
      newRules,
      `${INSURANCE_PLAN_PARAMS_RULES.DELTA_END}.value`,
      deltaEndValue,
    );
  }

  const deltaEndRiaValue = calculateDeltaEndValue(
    newRules,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_START_RIA,
    INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA,
  );
  if (deltaEndRiaValue !== null) {
    set(
      newRules,
      `${INSURANCE_PLAN_PARAMS_RULES.DELTA_END_RIA}.value`,
      deltaEndRiaValue,
    );
  }

  const quoteValidityConfig = serializeQuoteValidityConfig(newRules);
  if (quoteValidityConfig !== null) {
    set(
      newRules,
      `${INSURANCE_PLAN_PARAMS_RULES.QUOTE_VALIDITY}.value`,
      quoteValidityConfig,
    );
  }
  return newRules;
};

const convertRulesToEntries = (rules) => {
  return Object.entries(rules).map(([name, data]) => ({
    id: get(data, "id") || cuid(),
    name,
    value: get(data, "value"),
  }));
};

const EditHealth = ({ risk }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [offerLoading, setOfferLoading] = useState(true);
  const {
    user: {
      organization: { id: organizationId },
    },
  } = useContext(StoreContext);

  const { data: organization } = useQuery(graphql.queries.ORGANIZATION, {
    variables: { where: { id: organizationId } },
  });

  const refetchAttachments = [
    {
      query: graphql.queries.ATTACHMENTS,
      variables: {
        where: {
          insurancePlans: { id },
        },
        isIn: { type: Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES) },
      },
    },
  ];
  const [addAttachment] = useMutation(CREATE_ATTACHMENT, {
    refetchQueries: refetchAttachments,
  });
  const [unlinkAttachmentFromInsurancePlan] = useMutation(
    UNLINK_ATTACHMENT_FROM_INSURANCE_PLAN,
    {
      refetchQueries: refetchAttachments,
    },
  );

  const cancel = () =>
    navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path);

  const [updateInsurancePlan] = useMutation(
    graphql.mutations.UPDATE_INSURANCE_PLAN,
    {
      refetchQueries: [
        {
          query: graphql.queries.INSURANCE_PLANS,
          awaitRefetchQueries: true,
          variables: INITIAL_VALUES,
        },
        {
          query: graphql.queries.INSURANCE_PLANS,
          awaitRefetchQueries: true,
        },
        {
          query: graphql.queries.INSURANCE_PLAN,
          awaitRefetchQueries: true,
          variables: { where: { id } },
        },
      ],
      onCompleted: () =>
        navigate(SCREENS.PRIVATE.ADMIN.INSURANCE_PLANS.LIST.path),
    },
  );
  const { data: attachmentsData, loading: attachmentsLoading } = useQuery(
    ATTACHMENTS,
    {
      variables: {
        where: {
          insurancePlans: { id },
        },
        isIn: { type: Object.values(INSURANCE_PLAN_ATTACHMENTS_TYPES) },
      },
      fetchPolicy: "network-only",
    },
  );

  const { data, loading, error } = useQuery(graphql.queries.INSURANCE_PLAN, {
    variables: { where: { id } },
  });

  const [getOffers, { data: offers, loading: offersLoading, refetch }] =
    useLazyQuery(OFFERS);
  const { loading: mainOffersLoading } = useQuery(OFFERS, {
    variables: {
      where: {
        AND: [
          {
            offerInsurancePlans: {
              insurancePlan: {
                id,
              },
            },
          },
          {
            offerInsurancePlans: {
              main: true,
            },
          },
        ],
      },
    },
    onCompleted: ({ offers }) =>
      getOffers({
        fetchPolicy: "no-cache",
        variables: {
          where: {
            offerInsurancePlans: {
              main: false,
            },
          },
          isIn: { id: get(offers, "data", []).map(({ id }) => id) },
        },
        onCompleted: () => setOfferLoading(false),
      }),
  });

  const [addOffer] = useMutation(CREATE_OFFER, {});

  const [deleteOffer] = useMutation(DELETE_OFFER, {});

  const onSubmit = async (values) => {
    const {
      date,
      params,
      documentSteps,
      commissions,
      iconAttachment,
      attachments,
      newAttachments,
      level,
      offers = [],
      initialOffers = [],
      zones: _zones,
      ageBrackets: _ageBrackets,
      regimes: _regimes,
      insurancePlanCoverages: _insurancePlanCoverages,
      icon: _icon,
      ...rest
    } = values;

    const documentStepsValue = (attachments || []).reduce((acc, attachment) => {
      const attachmentId = get(attachment, "id");
      if (!attachmentId) return acc;
      acc[attachmentId] = {
        [DOCUMENT_STEPS.DEVIS]: get(
          documentSteps,
          `${attachmentId}.${DOCUMENT_STEPS.DEVIS}`,
          DEFAULT_DOCUMENT_STEP_FLAGS[DOCUMENT_STEPS.DEVIS],
        ),
        [DOCUMENT_STEPS.CONTRAT]: get(
          documentSteps,
          `${attachmentId}.${DOCUMENT_STEPS.CONTRAT}`,
          DEFAULT_DOCUMENT_STEP_FLAGS[DOCUMENT_STEPS.CONTRAT],
        ),
        [DOCUMENT_STEPS.SIGNATURE]: get(
          documentSteps,
          `${attachmentId}.${DOCUMENT_STEPS.SIGNATURE}`,
          DEFAULT_DOCUMENT_STEP_FLAGS[DOCUMENT_STEPS.SIGNATURE],
        ),
      };
      return acc;
    }, {});
    const buildDocumentRules = (item) => ({
      [INSURANCE_PLAN_PARAMS_RULES.DOCUMENT_STEPS]: {
        id:
          get(item, `rules.${INSURANCE_PLAN_PARAMS_RULES.DOCUMENT_STEPS}.id`) ||
          cuid(),
        name: INSURANCE_PLAN_PARAMS_RULES.DOCUMENT_STEPS,
        value: documentStepsValue,
      },
    });
    const paramsWithDocumentRules =
      params.length > 0
        ? params.map((item, index) => {
            if (index !== 0) return item;
            return {
              ...item,
              rules: {
                ...item.rules,
                ...buildDocumentRules(item),
              },
            };
          })
        : [
            {
              id: cuid(),
              rules: buildDocumentRules({}),
            },
          ];

    const updatedParams = paramsWithDocumentRules
      .map((item) => ({
        id: item.id,
        rules: item.rules,
      }))
      .filter((item) => !isEmpty(item.rules))
      .map(({ id, rules }) => {
        return {
          id,
          rules: convertRulesToEntries(
            updateRulesWithDeltaValues(rules),
          )?.filter(
            ({ value }) =>
              isNumber(value) || isBoolean(value) || !isEmpty(value),
          ),
        };
      });
    const offerIds = offers.map((offer) => offer.id);
    const offersToDelete = initialOffers.filter(
      (initialOffer) => !offerIds.includes(initialOffer),
    );
    await Promise.all(
      offersToDelete.map((offer) =>
        deleteOffer({
          variables: { where: { id: offer } },
        }),
      ),
    );

    await Promise.all(
      offers.map((offer) =>
        addOffer({
          variables: {
            data: {
              ...offer,
              processes: get(offer, "processes", []).map((id) => ({ id })),
              offerInsurancePlans: [
                ...get(offer, "offerInsurancePlans", []),
                { id: cuid(), main: true, insurancePlan: { id } },
              ],
            },
          },
        }),
      ),
    );

    await refetch();
    return updateInsurancePlan({
      variables: {
        where: { id },
        data: {
          ...rest,
          level: isNumber(level) ? level : null,
          startDate: get(date, "0") || null,
          endDate: get(date, "1") || null,
          icon: iconAttachment,
          params: updatedParams,
          commissions: commissions.map((commission) => ({ id: commission })),
        },
      },
    });
  };

  if (
    loading ||
    attachmentsLoading ||
    offersLoading ||
    mainOffersLoading ||
    offerLoading
  )
    return <Loading />;
  if (error) return "Error occurred";

  return (
    <View
      onSubmit={onSubmit}
      data={omitDeep(data?.insurancePlan, "__typename")}
      attachments={omitDeep(
        get(attachmentsData, "attachments.data", []),
        "__typename",
      )}
      offers={omitDeep(get(offers, "offers.data", []), "__typename")}
      cancel={cancel}
      addAttachment={addAttachment}
      unlinkAttachmentFromInsurancePlan={unlinkAttachmentFromInsurancePlan}
      config={get(organization, "organization.config", {})}
      risk={risk}
    />
  );
};

export default EditHealth;
